import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatDate, formatRelative } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ListChecks, Plus, CheckCircle2, Clock, AlertTriangle, Search, Filter, Trash2, Loader2,
} from "lucide-react";
import CreateTaskModal from "../components/tasks/CreateTaskModal";
import { toast } from "sonner";

const PRIORITY_CFG = {
  high:   { bg: "#FEE2E2", text: "#B91C1C", label: "High" },
  medium: { bg: "#FEF3C7", text: "#B45309", label: "Med" },
  low:    { bg: "#F3F4F6", text: "#374151", label: "Low" },
};
const STATUS_CFG = {
  open:        { bg: "#EFF6FF", text: "#1D4ED8", label: "Open" },
  in_progress: { bg: "#FEF9C3", text: "#A16207", label: "In progress" },
  done:        { bg: "#DCFCE7", text: "#166534", label: "Done" },
  snoozed:     { bg: "#F3F4F6", text: "#4B5563", label: "Snoozed" },
};

export default function TasksIndex() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [show, setShow] = useState(false);

  useEffect(() => { load(); }, [statusFilter]);
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter === "active") params.status = "open,in_progress";
      else if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get(`/operations/tasks`, { params });
      setTasks(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => tasks.filter((t) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (t.title || "").toLowerCase().includes(s) ||
           (t.assignee_email || "").toLowerCase().includes(s) ||
           (t.related_client_name || "").toLowerCase().includes(s);
  }), [tasks, q]);

  const stats = useMemo(() => {
    const open = tasks.filter((t) => t.status === "open" || t.status === "in_progress");
    const now = new Date().toISOString();
    const overdue = open.filter((t) => t.due_date && t.due_date < now);
    return { open: open.length, overdue: overdue.length };
  }, [tasks]);

  const complete = async (id) => {
    try {
      await api.post(`/operations/tasks/${id}/complete`);
      toast.success("Marked done");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/operations/tasks/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1300px] mx-auto" data-testid="tasks-index">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-1">
            <ListChecks className="w-3.5 h-3.5" /> Operations tasks
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Tasks</h1>
          <p className="text-sm text-[#6B7280] mt-1">Track follow-ups, assignments, and deadlines tied to specific loans, clients, or payoffs.</p>
        </div>
        <Button onClick={() => setShow(true)} className="bg-[#0066FF] hover:bg-[#0052CC] h-10" data-testid="new-task-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New task
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Stat label="Open tasks" value={stats.open} accent />
        <Stat label="Overdue" value={stats.overdue} warn={stats.overdue > 0} />
        <Stat label="Total (all time)" value={tasks.length} />
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, client, assignee…" className="pl-9 h-9" data-testid="tasks-search" />
          </div>
          <div className="flex items-center gap-1 text-xs">
            <Filter className="w-3.5 h-3.5 text-[#6B7280] mr-1" />
            {[
              ["active", "Active"], ["open", "Open"], ["in_progress", "In progress"],
              ["done", "Done"], ["snoozed", "Snoozed"], ["all", "All"],
            ].map(([k, l]) => (
              <button key={k} onClick={() => setStatusFilter(k)}
                data-testid={`tasks-filter-${k}`}
                className={`px-2.5 py-1 rounded-md font-medium transition-colors ${statusFilter === k ? "bg-[#0A2540] text-white" : "text-[#6B7280] hover:bg-[#F9FAFB]"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-[#6B7280] flex items-center justify-center gap-2" data-testid="tasks-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading tasks…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-16 text-center" data-testid="tasks-empty">
            <div className="inline-flex flex-col items-center text-[#6B7280]">
              <div className="w-12 h-12 rounded-full bg-[#F0F6FF] flex items-center justify-center mb-3"><ListChecks className="w-5 h-5 text-[#0066FF]" /></div>
              <div className="text-sm font-medium text-[#0A2540]">No tasks yet</div>
              <div className="text-xs mt-0.5">Create one to get started.</div>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-[#F3F4F6]">
            {filtered.map((t) => {
              const pri = PRIORITY_CFG[t.priority] || PRIORITY_CFG.medium;
              const st = STATUS_CFG[t.status] || STATUS_CFG.open;
              const overdue = t.due_date && t.due_date < new Date().toISOString() && (t.status === "open" || t.status === "in_progress");
              return (
                <li key={t.task_id} className="px-4 py-3 hover:bg-[#F9FAFB] flex items-start gap-3" data-testid={`task-row-${t.task_id}`}>
                  <button onClick={() => complete(t.task_id)} disabled={t.status === "done"}
                    className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${t.status === "done" ? "bg-[#0066FF] border-[#0066FF]" : "border-[#D1D5DB] hover:border-[#0066FF]"}`}
                    data-testid={`task-complete-${t.task_id}`}>
                    {t.status === "done" && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${t.status === "done" ? "text-[#9CA3AF] line-through" : "text-[#0A2540]"}`}>{t.title}</div>
                    {t.description && <div className="text-xs text-[#6B7280] mt-0.5">{t.description}</div>}
                    <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[11px]">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: pri.bg, color: pri.text }}>{pri.label}</span>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: st.bg, color: st.text }}>{st.label}</span>
                      {t.assignee_email && <span className="text-[#6B7280]">@{t.assignee_email.split("@")[0]}</span>}
                      {t.due_date && (
                        <span className={`inline-flex items-center gap-1 tabular-nums ${overdue ? "text-[#B91C1C]" : "text-[#6B7280]"}`}>
                          <Clock className="w-3 h-3" /> Due {formatDate(t.due_date)}{overdue && " (overdue)"}
                        </span>
                      )}
                      {t.related_client_name && <Link to={`/admin/clients/${encodeURIComponent(t.related_client_name)}`} className="text-[#0066FF] hover:underline">{t.related_client_name}</Link>}
                      {t.related_loan_id && <Link to={`/admin/loans/${t.related_loan_id}`} className="text-[#0066FF] hover:underline">{t.related_loan_id}</Link>}
                      {t.related_payoff_id && <Link to={`/admin/payoffs/${t.related_payoff_id}`} className="text-[#0066FF] hover:underline">{t.related_payoff_id}</Link>}
                      <span className="text-[#9CA3AF] ml-auto">created {formatRelative(t.created_at)}</span>
                    </div>
                  </div>
                  <button onClick={() => remove(t.task_id)} className="opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity text-[#9CA3AF] hover:text-[#B91C1C] p-1" data-testid={`task-delete-${t.task_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <CreateTaskModal open={show} onClose={() => setShow(false)} onCreated={() => load()} />
    </div>
  );
}

function Stat({ label, value, accent, warn }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">{label}</div>
      <div className={`tabular-nums font-display text-2xl font-semibold ${warn ? "text-[#B91C1C]" : accent ? "text-[#0066FF]" : "text-[#0A2540]"}`}>{value}</div>
    </div>
  );
}
